import moment from 'moment';
import Cookies from 'js-cookie';
import {
  breakfastType,
  dinnerType,
  getTimes,
  entertainmentType,
  getTimeSlotIndexById,
  getDiningColor,
  filterPastEvents,
  getLatestEntertainmentBookableDate,
  isSplash,
  isNowAfterTime,
  getSlotColor,
} from '../../utils';
import {
  SET_BOOKED_DININGS,
  SET_BOOKED_ENTERTAINMENTS,
  SET_AVAILABLE_ENTERTAINMENTS,
} from '../constants/bookedEvents';
import { getIsDineAroundEvent, getDineAroundTypeByEntertainment } from '../../dineAround/dineAroundHelper';

const formatDining = (bookedDining) => {
  if (!bookedDining || !bookedDining.AddonCategory) return {};
  const { AddonCategory, Addon = {} } = bookedDining;
  const { comments = '', code } = Addon;
  const { startTime, endTime } = getTimes(comments);
  const mainGuestFirstName = Cookies.get('_mainGuestFirstName');
  const numOfGuests = Cookies.get('_numOfGuests');
  return {
    id: code.toLocaleLowerCase().split(' ').join('-'),
    name: AddonCategory === breakfastType ? 'Breakfast' : 'Dinner',
    startTime,
    endTime,
    slotColor: getDiningColor(code),
    mainGuestFirstName,
    numOfGuests: parseInt(numOfGuests, 10),
    type: AddonCategory,
    isBMEItem: true,
  };
};

const getEntertainmentStartTime = (entertainmentStart) => {
  const hours = entertainmentStart.hours();
  const minutes = entertainmentStart.minutes();
  return minutes < 10 ? `${hours}:0${minutes}` : `${hours}:${minutes}`;
};

const formatEntertainment = ({
  ActivityId, ActivityName, Location, Time, TimeSlotId, CurrentParticipants = 1, isBMEItem = false, isPerPersonActivity,
}) => {
  const entertainmentStart = moment(Time);
  const mainGuestFirstName = Cookies.get('_mainGuestFirstName');
  const numOfGuests = Cookies.get('_numOfGuests');
  return {
    activityId: ActivityId,
    id: `${TimeSlotId}`,
    name: ActivityName,
    location: Location,
    startTime: getEntertainmentStartTime(entertainmentStart),
    mainGuestFirstName,
    numOfGuests: isSplash(ActivityName) || isPerPersonActivity ? CurrentParticipants : parseInt(numOfGuests, 10),
    startDate: entertainmentStart.format('YYYY-MM-DD'),
    type: entertainmentType,
    isBMEItem,
  };
};

const addDiningToEvents = (dinings, formattedDining, startDate) => {
  const diningCopy = { ...formattedDining };
  diningCopy.startDate = startDate;
  dinings.push(diningCopy);
};

const getHolidayDates = () => {
  const arrivalDateString = Cookies.get('_arrivalDate');
  const departureDateString = Cookies.get('_departureDate');
  const arrivalDate = moment(arrivalDateString, 'YYYY-MM-DD');
  const departureDate = moment(departureDateString, 'YYYY-MM-DD');
  return { arrivalDate, departureDate };
};

const getAvailableEntertainmentForTheEvent = (availableEntertainments, activityId) => availableEntertainments.find(({ Id }) => activityId === Id) || {};

const getEntertainmentEndTime = (startTime, duration) => {
  const [startHourString = '', startMinuteString = ''] = startTime.split(':');
  const startHour = parseInt(startHourString, 10);
  const startMinute = parseInt(startMinuteString, 10);
  const hour = Math.floor(duration / 60);
  const minute = duration % 60;
  const endHour = startHour + hour;
  const endMinute = startMinute + minute;
  if (endMinute < 60) return `${endHour}:${endMinute < 10 ? `0${endMinute}` : endMinute}`;
  const plusHour = Math.floor(endMinute / 60);
  const plusMinute = endMinute % 60;
  const finalMinute = plusMinute < 10 ? `0${plusMinute}` : plusMinute;
  return plusMinute === 60 ? `${endHour + plusHour + 1}:00` : `${endHour + plusHour}:${finalMinute}`;
};

const initialState = {
  events: [],
  eventsWithPastEvents: [],
};

const bookedEvents = (state = initialState, action) => {
  switch (action.type) {
    case SET_BOOKED_DININGS: {
      const { events, eventsWithPastEvents } = state;
      const { bookedBreakfast, bookedDinner } = action;
      const formattedBreakfast = formatDining(bookedBreakfast);
      const formattedDinner = formatDining(bookedDinner);
      const { arrivalDate, departureDate } = getHolidayDates();
      const dinings = [];

      let currentDate = moment(arrivalDate);
      for (currentDate; currentDate.isSameOrBefore(departureDate, 'day'); currentDate = currentDate.add(1, 'day')) {
        const startDate = currentDate.format('YYYY-MM-DD');
        if (bookedBreakfast && bookedBreakfast.AddonCategory && !currentDate.isSame(moment(arrivalDate), 'day')) {
          addDiningToEvents(dinings, formattedBreakfast, startDate);
        }
        if (bookedDinner && bookedDinner.AddonCategory && !currentDate.isSame(moment(departureDate), 'day')) {
          addDiningToEvents(dinings, formattedDinner, startDate);
        }
      }

      const bookedEntertainments = events.filter(({ type }) => type !== breakfastType && type !== dinnerType);
      const newEventsWithoutPastEvents = [...bookedEntertainments, ...dinings].filter(filterPastEvents);

      const diningsWithPastFlag = dinings.map((dining) => {
        const { startDate, endTime } = dining;
        return { ...dining, isPastEvent: isNowAfterTime(startDate, endTime) };
      });

      return { ...state, events: newEventsWithoutPastEvents, eventsWithPastEvents: [...eventsWithPastEvents, ...diningsWithPastFlag] };
    }
    case SET_BOOKED_ENTERTAINMENTS: {
      const { bookedEntertainments, latestActivityBookingDate } = action;
      const { events } = state;
      const departureDate = getLatestEntertainmentBookableDate(Cookies.get('_departureDate'), latestActivityBookingDate);
      const momentDepartureDate = moment(departureDate);
      const entertainments = bookedEntertainments
        .filter(({ Time }) => {
          const entertainmentStart = moment(Time);
          return momentDepartureDate.diff(entertainmentStart, 'day') >= 0;
        })
        .map(formatEntertainment);
      const bookedDiningEvents = events.filter(({ type }) => type === breakfastType || type === dinnerType);

      return { ...state, events: [...bookedDiningEvents, ...entertainments] };
    }
    case SET_AVAILABLE_ENTERTAINMENTS: {
      const { events } = state;
      const {
        availableEntertainments,
        arrivalTime,
        latestBookingDateEndTime,
        departureTime,
        latestActivityBookingDate,
        isDaBooking,
        dineAroundBreakfastCategory,
        dineAroundDinnerCategory,
      } = action;
      const eventsWithSlotColorsAndPastEvents = events
        .map((event) => {
          const { id, slotColor, startDate, startTime, activityId } = event;
          const { Duration: duration = 60, CategoryId: categoryId } = getAvailableEntertainmentForTheEvent(availableEntertainments, activityId);
          const isDineAroundItem = getIsDineAroundEvent(categoryId, isDaBooking, dineAroundBreakfastCategory, dineAroundDinnerCategory);
          const dineAroundType = getDineAroundTypeByEntertainment(categoryId, dineAroundBreakfastCategory, dineAroundDinnerCategory);
          const endTime = getEntertainmentEndTime(startTime, duration);
          const isPastEvent = isNowAfterTime(startDate, endTime);
          const slotIndex = getTimeSlotIndexById(availableEntertainments, id, arrivalTime, latestBookingDateEndTime, departureTime, latestActivityBookingDate);
          const bookedEvent = { ...event, endTime, isPastEvent, isDineAroundItem, dineAroundType };
          if (slotColor || slotIndex < 0) return bookedEvent;
          return { ...bookedEvent, slotColor: getSlotColor(slotIndex) };
        });

      return { ...state, events: eventsWithSlotColorsAndPastEvents.filter(filterPastEvents), eventsWithPastEvents: eventsWithSlotColorsAndPastEvents };
    }
    default:
      return state;
  }
};

export default bookedEvents;
